import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { SelectOption } from '@rsos/base-components/Select';
import { Select } from '@rsos/components/capstone/base';
import { useUpdatePreconfiguredMessageMutation } from '@rsos/reduxDataStore/src/features/preconfiguredMessages';
import type { PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import { PreparedResponseType } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesTypes';
import { isAuthorityMessage } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesUtils';
import { organizationSelector } from '@rsos/reduxDataStore/src/features/selectors/organizationSelector';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import { AppState } from '../../../types';
import { UPDATE_SUCCESS_MESSAGE } from '../modals/utils';
import styles from './AssignedMessages.module.css';

export interface AssignedMessageSlot {
  /** The type stamped on the selected message. */
  type: PreparedResponseType;
  /**
   * Name of the action this slot backs (e.g. "End Chat"). Shown next to the
   * select and used to build the select's accessible label and failure toasts.
   */
  label: string;
  /**
   * Optional additional filter applied to the selectable options, e.g.
   * restricting to messages that contain a required smart phrase.
   */
  messageFilter?: (message: PreconfiguredResponse) => boolean;
}

interface Props {
  slot: AssignedMessageSlot;
  messages: PreconfiguredResponse[];
}

const AssignedMessageRow = ({ slot, messages }: Props) => {
  const orgName =
    useSelector((state: AppState) => organizationSelector(state))?.name || '';
  const [updatePreconfiguredMessage, { isLoading }] =
    useUpdatePreconfiguredMessageMutation();

  const currentMessage = useMemo(
    () => messages.find(m => m.type === slot.type && isAuthorityMessage(m)),
    [messages, slot.type],
  );

  const options: SelectOption<string>[] = useMemo(
    () =>
      messages
        .filter(
          m =>
            isAuthorityMessage(m) &&
            m.enabled &&
            (slot.messageFilter ? slot.messageFilter(m) : true),
        )
        .map(m => ({ value: m.id, label: m.title })),
    [messages, slot],
  );

  const selectedOption = currentMessage
    ? { value: currentMessage.id, label: currentMessage.title }
    : null;

  // Derive the accessible label and failure toasts from the action name so
  // each slot only has to declare it once.
  const actionName = slot.label.toLowerCase();

  const handleChange = (option: SelectOption<string> | null) => {
    if (option === null) {
      if (!currentMessage) return;

      updatePreconfiguredMessage({
        org: orgName,
        id: currentMessage.id,
        updates: { type: PreparedResponseType.custom },
      })
        .unwrap()
        .then(() => notifySuccess(UPDATE_SUCCESS_MESSAGE))
        .catch(() => notifyError(`Failed to clear ${actionName} message`));
      return;
    }

    updatePreconfiguredMessage({
      org: orgName,
      id: option.value,
      updates: { type: slot.type },
    })
      .unwrap()
      .then(() => notifySuccess(UPDATE_SUCCESS_MESSAGE))
      .catch(() => notifyError(`Failed to set ${actionName} message`));
  };

  return (
    <div className={styles.selectRow}>
      <span className={styles.selectLabel}>{slot.label}</span>
      <div className={styles.selectWrapper}>
        <Select
          options={options}
          value={selectedOption}
          onChange={handleChange}
          placeholder="Default system message"
          isClearable
          isDisabled={isLoading || options.length === 0}
          aria-label={`Select ${actionName} message`}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </div>
    </div>
  );
};

export default AssignedMessageRow;
